import { useState, useEffect, useRef, useCallback } from 'react';

const books = [
  {
    id: 'river',
    badge: 'New Release — Available Now on Amazon',
    title: ['A River', 'PENETRATING', 'THE Soul Line'],
    titleHighlight: 'PENETRATING',
    subtitle: 'Book #II — by Kevin Hamlett',
    description:
      'A river twists, whirls, and bursts across the shore — lavish, unstoppable, alive. In the calm of daylight, it pours again and again, a cascade that mirrors the currents of our own minds. Each drop awakens thought, stirs the heart, and calls us to dive in.',
    price: '$3.99',
    amazonUrl: 'https://www.amazon.com/dp/B0GT29CWSG',
    coverUrl:
      'https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/0ca6a10095a01aaf768b6e10e53d5c75.jpeg',
    coverAlt: 'A River PENETRATING THE Soul Line - Book Cover by Kevin Hamlett',
    bgUrl:
      'https://public.readdy.ai/ai/img_res/fcbf78dbeb42798578df05784cf02420.jpg',
    bgAlt: 'River landscape background',
    publisher: 'Leavitt Peak Press',
    publishDate: 'March 18, 2026',
    pages: '76 pages',
    excerptId: '#excerpt',
    stars: 5,
  },
  {
    id: 'endless',
    badge: 'Also by Kevin Hamlett — Available on Amazon',
    title: ['Endless Hope:', 'A Look at', 'Glaring Reality'],
    titleHighlight: 'Glaring Reality',
    subtitle: 'Standalone — by Kevin Hamlett',
    description:
      "Endless Hope shines a light on the universal struggles we all face. At its core, humanity shares a timeless pursuit — for unshakeable faith, enduring hope, and unconditional love. A concise collection of thought-provoking poems and poignant reflections that capture the urgency of life's journey.",
    price: '$3.38',
    amazonUrl: 'https://www.amazon.com/dp/B0FQRNYL93',
    coverUrl:
      'https://readdy.ai/api/search-image?query=book%20cover%20design%20for%20spiritual%20inspirational%20poetry%20book%20titled%20Endless%20Hope%20A%20Look%20at%20Glaring%20Reality%20by%20Kevin%20Edward%20Hamlett%2C%20featuring%20a%20lone%20figure%20standing%20on%20a%20hilltop%20at%20golden%20sunrise%2C%20warm%20amber%20and%20golden%20light%20flooding%20the%20horizon%2C%20vast%20open%20sky%20with%20soft%20clouds%2C%20hopeful%20and%20uplifting%20mood%2C%20painterly%20artistic%20style%2C%20rich%20warm%20tones%20of%20gold%20amber%20and%20soft%20cream%2C%20professional%20book%20cover%20composition%20with%20clear%20title%20space%2C%20deep%20atmospheric%20spiritual%20mood%2C%20simple%20clean%20background&width=400&height=560&seq=endless-hope-cover-001&orientation=portrait',
    coverAlt: 'Endless Hope: A Look at Glaring Reality - Book Cover by Kevin Hamlett',
    bgUrl:
      'https://readdy.ai/api/search-image?query=breathtaking%20golden%20sunrise%20over%20vast%20open%20hilltop%20landscape%2C%20lone%20silhouette%20figure%20standing%20at%20the%20peak%2C%20warm%20amber%20golden%20light%20flooding%20the%20horizon%2C%20soft%20clouds%20drifting%20across%20a%20wide%20open%20sky%2C%20hopeful%20uplifting%20spiritual%20atmosphere%2C%20painterly%20impressionistic%20style%2C%20rich%20warm%20tones%20of%20gold%20amber%20cream%20and%20soft%20orange%2C%20cinematic%20wide%20composition%2C%20serene%20and%20peaceful%20mood&width=1920&height=1080&seq=hero-endless-hope-bg-001&orientation=landscape',
    bgAlt: 'Golden sunrise hilltop background',
    publisher: 'Leavitt Peak Press',
    publishDate: 'September 10, 2025',
    pages: '63 pages',
    excerptId: '#other-books',
    stars: 5,
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const contentRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goTo = useCallback(
    (index: number, dir: 'left' | 'right') => {
      if (animating) return;
      setDirection(dir);
      setAnimating(true);
      setTimeout(() => {
        setCurrent(index);
        setAnimating(false);
      }, 400);
    },
    [animating]
  );

  const prev = useCallback(() => {
    const idx = (current - 1 + books.length) % books.length;
    goTo(idx, 'left');
  }, [current, goTo]);

  const next = useCallback(() => {
    const idx = (current + 1) % books.length;
    goTo(idx, 'right');
  }, [current, goTo]);

  // Auto-advance every 8s
  useEffect(() => {
    timerRef.current = setTimeout(() => next(), 8000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [current, next]);

  // Entrance animation
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(30px)';
    setTimeout(() => {
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    }, 100);
  }, [current]);

  const book = books[current];

  const slideStyle: React.CSSProperties = {
    opacity: animating ? 0 : 1,
    transform: animating
      ? `translateX(${direction === 'right' ? '-40px' : '40px'})`
      : 'translateX(0)',
    transition: 'opacity 0.4s ease, transform 0.4s ease',
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background — crossfade on change */}
      {books.map((b, i) => (
        <div
          key={b.id}
          className="absolute inset-0 transition-opacity duration-700"
          style={{ opacity: i === current ? 1 : 0, zIndex: 0 }}
        >
          <img
            src={b.bgUrl}
            alt={b.bgAlt}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/90 via-stone-950/60 to-stone-950/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-transparent to-stone-950/40"></div>
        </div>
      ))}

      {/* Decorative glow */}
      <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none z-10"></div>

      {/* Content */}
      <div
        className="relative z-20 w-full max-w-7xl mx-auto px-4 md:px-10 flex flex-col lg:flex-row items-center gap-12 lg:gap-20 pt-24 pb-16"
        style={slideStyle}
      >
        {/* Left: Text */}
        <div ref={contentRef} className="flex-1 text-center lg:text-left">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-300 text-xs font-semibold mb-6 backdrop-blur-sm">
            <i className="ri-book-open-line text-sm"></i>
            {book.badge}
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white leading-tight mb-4">
            {book.title.map((line, i) =>
              line.includes(book.titleHighlight) ? (
                <span key={i}>
                  {line.replace(book.titleHighlight, '')}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">
                    {book.titleHighlight}
                  </span>
                  <br />
                </span>
              ) : (
                <span key={i}>
                  {line}
                  <br />
                </span>
              )
            )}
          </h1>

          <p className="text-stone-300 text-base md:text-lg font-light italic mb-2 tracking-wide">
            {book.subtitle}
          </p>

          <div className="w-16 h-0.5 bg-amber-400 my-6 mx-auto lg:mx-0"></div>

          <p className="text-stone-300 text-base md:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0 mb-10">
            {book.description}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
            <a
              href={book.amazonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-4 rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-base transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-amazon-line text-lg"></i>
              Buy on Amazon — {book.price}
            </a>
            <button
              onClick={() => {
                const el = document.querySelector(book.excerptId);
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="w-full sm:w-auto px-8 py-4 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold text-base transition-all duration-200 cursor-pointer whitespace-nowrap backdrop-blur-sm flex items-center justify-center gap-2"
            >
              <i className="ri-eye-line text-lg"></i>
              {book.id === 'river' ? 'Read an Excerpt' : 'Learn More'}
            </button>
          </div>

          {/* Publisher badge */}
          <div className="mt-10 flex items-center gap-3 justify-center lg:justify-start">
            <span className="text-stone-500 text-xs uppercase tracking-widest">Published by</span>
            <span className="text-stone-300 text-sm font-medium">{book.publisher}</span>
            <span className="text-stone-600">·</span>
            <span className="text-stone-400 text-xs">{book.publishDate}</span>
          </div>
        </div>

        {/* Right: Book Cover */}
        <div className="flex-shrink-0 flex flex-col items-center gap-6">
          <div className="relative group">
            <div className="absolute -inset-4 bg-amber-400/20 rounded-2xl blur-2xl group-hover:bg-amber-400/30 transition-all duration-500"></div>
            <img
              src={book.coverUrl}
              alt={book.coverAlt}
              className="relative w-56 md:w-72 h-auto rounded-lg object-cover object-top transform group-hover:-translate-y-2 transition-transform duration-500"
            />
          </div>
          {/* Stars + pages */}
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-1">
              {Array.from({ length: book.stars }).map((_, i) => (
                <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>
              ))}
            </div>
            <span className="text-stone-400 text-xs">Kindle Edition · {book.pages}</span>
          </div>
        </div>
      </div>

      {/* LEFT Arrow */}
      <button
        onClick={prev}
        aria-label="Previous book"
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-amber-500/30 border border-white/20 hover:border-amber-400/50 text-white transition-all duration-200 cursor-pointer backdrop-blur-sm"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>

      {/* RIGHT Arrow */}
      <button
        onClick={next}
        aria-label="Next book"
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-amber-500/30 border border-white/20 hover:border-amber-400/50 text-white transition-all duration-200 cursor-pointer backdrop-blur-sm"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      {/* Dot indicators */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3">
        {books.map((b, i) => (
          <button
            key={b.id}
            onClick={() => goTo(i, i > current ? 'right' : 'left')}
            aria-label={`Go to book ${i + 1}`}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === current
                ? 'w-8 h-2 bg-amber-400'
                : 'w-2 h-2 bg-white/30 hover:bg-white/60'
            }`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/40 animate-bounce z-30">
        <span className="text-xs tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-sm"></i>
      </div>
    </section>
  );
}
